import { Contact, ContactType, ExternalCompany } from '../api/CRMApiClient';
import { ContactValidationKind } from '../enums/ContactValidationKind';

export interface CompanyDuplicateCheckResult {
  exactMatch?: ExternalCompany;
  similarMatches: ExternalCompany[];
}

export interface ContactDuplicateCheckResult {
  exactMatch?: Contact;
  similarMatches: Contact[];
}

export const normalizeString = (val?: string): string => {
  if (!val) return '';
  return val.trim().toLowerCase().replace(/\s+/g, ' ');
};

export const normalizeDigits = (val?: string): string => {
  if (!val) return '';
  return val.replace(/\D/g, '');
};

export const normalizeSocialHandle = (val?: string): string => {
  if (!val) return '';
  let cleaned = val.trim().toLowerCase();
  // Remove URLs
  cleaned = cleaned.replace(/^https?:\/\/(www\.)?/, '');
  cleaned = cleaned.replace(/^(t\.me|telegram\.me|instagram\.com|facebook\.com|linkedin\.com\/in|viber:\/\/)\/?/, '');
  // Remove leading @
  cleaned = cleaned.replace(/^@+/, '');
  // Remove trailing slashes or queries
  cleaned = cleaned.replace(/\/.*$/, '').replace(/\?.*$/, '').trim();
  return cleaned;
};

/**
 * Searches for exact or similar companies by Name within the tenant
 */
export const findSimilarCompanies = (
  query: string,
  companies: ExternalCompany[] = [],
  excludeCompanyId?: number | null,
): CompanyDuplicateCheckResult => {
  const normQuery = normalizeString(query);
  if (!normQuery || normQuery.length < 2) {
    return { similarMatches: [] };
  }

  const list = companies.filter(c => c.id !== excludeCompanyId && Boolean(c.name));
  let exactMatch: ExternalCompany | undefined;
  const similarMatches: ExternalCompany[] = [];

  for (const comp of list) {
    const compNameNorm = normalizeString(comp.name);
    if (compNameNorm === normQuery) {
      exactMatch = comp;
    } else if (compNameNorm.includes(normQuery) || normQuery.includes(compNameNorm)) {
      similarMatches.push(comp);
    }
  }

  return { exactMatch, similarMatches };
};

/**
 * Searches for duplicate company by EDRPOU / Tax ID
 */
export const findDuplicateEdrpou = (
  edrpou: string,
  companies: ExternalCompany[] = [],
  excludeCompanyId?: number | null,
): ExternalCompany | undefined => {
  const digits = normalizeDigits(edrpou);
  if (!digits || digits.length < 4) {
    return undefined;
  }

  return companies.find(
    c => c.id !== excludeCompanyId && normalizeDigits(c.edrpou) === digits
  );
};

/**
 * Searches for similar contacts by Name and/or Surname
 */
export const findSimilarContacts = (
  name: string,
  surname: string,
  contacts: Contact[] = [],
  excludeContactId?: number | null,
): ContactDuplicateCheckResult => {
  const normName = normalizeString(name);
  const normSurname = normalizeString(surname);

  if ((!normName || normName.length < 2) && (!normSurname || normSurname.length < 2)) {
    return { similarMatches: [] };
  }

  const list = contacts.filter(c => c.id !== excludeContactId && (Boolean(c.name) || Boolean(c.surname)));
  let exactMatch: Contact | undefined;
  const similarMatches: Contact[] = [];

  for (const contact of list) {
    const cName = normalizeString(contact.name);
    const cSurname = normalizeString(contact.surname);

    const isExactName = normName && cName === normName;
    const isExactSurname = normSurname && cSurname === normSurname;

    if (isExactName && isExactSurname) {
      exactMatch = contact;
    } else if (
      (normName && normSurname && cName === normName && cSurname.includes(normSurname)) ||
      (normName && normSurname && cSurname === normSurname && cName.includes(normName)) ||
      (normName && !normSurname && cName.includes(normName)) ||
      (!normName && normSurname && cSurname.includes(normSurname)) ||
      (normName && cSurname.includes(normName)) ||
      (normSurname && cName.includes(normSurname))
    ) {
      similarMatches.push(contact);
    }
  }

  return { exactMatch, similarMatches };
};

/**
 * Searches for duplicate contact by Email
 */
export const findDuplicateContactEmail = (
  email: string,
  contacts: Contact[] = [],
  excludeContactId?: number | null,
): Contact | undefined => {
  const norm = normalizeString(email);
  if (!norm || norm.length < 3) {
    return undefined;
  }

  return contacts.find(
    c => c.id !== excludeContactId && normalizeString(c.email) === norm
  );
};

/**
 * Validates phone number format:
 * - For Ukrainian numbers (+380): exactly 12 digits total (+380 XX XXX XX XX)
 * - For Ukrainian domestic (0...): exactly 10 digits total (0XX XXX XX XX)
 * - For other international numbers: 9 to 15 digits
 */
export const isValidPhoneNumber = (value?: string): boolean => {
  if (!value) return true;
  const raw = value.trim();
  const digits = raw.replace(/\D/g, '');
  const validChars = /^[\d\s()+-]+$/.test(raw);
  if (!validChars) return false;

  if (digits.startsWith('380')) {
    return digits.length === 12;
  }
  if (digits.startsWith('0') && raw.startsWith('0')) {
    return digits.length === 10;
  }

  return digits.length >= 9 && digits.length <= 15;
};

/**
 * Searches for duplicate contact by Phone Number
 */
export const matchPhoneNumbers = (phone1?: string, phone2?: string): boolean => {
  if (!phone1 || !phone2) return false;
  const d1 = normalizeDigits(phone1);
  const d2 = normalizeDigits(phone2);
  if (d1.length < 9 || d2.length < 9) return false;

  // Exact full digits match
  if (d1 === d2) return true;

  // Exact national standard digits match (last 9 digits, e.g. 0680800689 vs +380680800689)
  return d1.slice(-9) === d2.slice(-9);
};

/**
 * Searches for duplicate contact by Phone Number
 */
export const findDuplicateContactPhone = (
  phone: string,
  contacts: Contact[] = [],
  excludeContactId?: number | null,
): Contact | undefined => {
  if (!phone) return undefined;
  return contacts.find(c => {
    if (c.id === excludeContactId || !c.phoneNumber) return false;
    return matchPhoneNumbers(phone, c.phoneNumber);
  });
};

export interface AdditionalInfoDuplicateResult {
  isInternalDuplicate?: boolean;
  externalContact?: Contact;
  matchedValue?: string;
}

/**
 * Checks if additional info is duplicated internally within the current form or externally across other contacts
 * based on the configured ContactType validationKind (Phone, Email, Social, Url).
 * If no validationKind is selected for this type, no validation is performed.
 */
export const checkAdditionalInfoDuplicate = (
  currentIndex: number,
  typeName: string,
  value: string,
  currentList: Array<{ type?: string; name?: string }> = [],
  contacts: Contact[] = [],
  contactTypes: ContactType[] = [],
  excludeContactId?: number | null,
  currentContactPhone?: string,
  currentContactEmail?: string,
): AdditionalInfoDuplicateResult | undefined => {
  const normType = normalizeString(typeName);
  const rawValue = (value || '').trim();

  if (!normType || !rawValue) {
    return undefined;
  }

  // Find the configured contact type metadata
  const typeConfig = contactTypes.find(ct => normalizeString(ct.name) === normType);
  const validationKind = typeConfig?.validationKind;

  // 1. Internal duplicate check within current contact's form list
  if (Array.isArray(currentList)) {
    for (let i = 0; i < currentList.length; i++) {
      if (i === currentIndex) continue;
      const other = currentList[i];
      if (!other || !other.type || !other.name) continue;

      const otherTypeNorm = normalizeString(other.type);
      const otherTypeConfig = contactTypes.find(ct => normalizeString(ct.name) === otherTypeNorm);
      const otherValidationKind = otherTypeConfig?.validationKind;

      if (
        validationKind === ContactValidationKind.PHONE &&
        (otherValidationKind === ContactValidationKind.PHONE || otherTypeNorm === normType)
      ) {
        if (matchPhoneNumbers(rawValue, other.name)) {
          return { isInternalDuplicate: true, matchedValue: other.name };
        }
      } else if (
        validationKind === ContactValidationKind.EMAIL &&
        (otherValidationKind === ContactValidationKind.EMAIL || otherTypeNorm === normType)
      ) {
        if (normalizeString(rawValue) === normalizeString(other.name)) {
          return { isInternalDuplicate: true, matchedValue: other.name };
        }
      } else if (
        validationKind === ContactValidationKind.SOCIAL &&
        otherTypeNorm === normType
      ) {
        if (normalizeSocialHandle(rawValue) === normalizeSocialHandle(other.name)) {
          return { isInternalDuplicate: true, matchedValue: other.name };
        }
      } else if (
        otherTypeNorm === normType &&
        normalizeString(other.name) === normalizeString(rawValue)
      ) {
        return { isInternalDuplicate: true, matchedValue: other.name };
      }
    }
  }

  // Check against the current contact's main phone and email fields
  if (currentContactPhone && validationKind === ContactValidationKind.PHONE) {
    if (matchPhoneNumbers(rawValue, currentContactPhone)) {
      return { isInternalDuplicate: true, matchedValue: currentContactPhone };
    }
  }

  if (currentContactEmail && validationKind === ContactValidationKind.EMAIL) {
    if (normalizeString(rawValue) === normalizeString(currentContactEmail)) {
      return { isInternalDuplicate: true, matchedValue: currentContactEmail };
    }
  }

  // If no validation kind is configured, do not check cross-contact duplicates
  if (!validationKind) {
    return undefined;
  }

  // 2. Perform cross-contact validation based on selected validationKind
  if (validationKind === ContactValidationKind.PHONE) {
    for (const contact of contacts) {
      if (contact.id === excludeContactId) continue;

      // Check main phoneNumber
      if (contact.phoneNumber && matchPhoneNumbers(rawValue, contact.phoneNumber)) {
        return { externalContact: contact, matchedValue: contact.phoneNumber };
      }

      // Check other additionalInfos with Phone validationKind or matching type
      if (contact.additionalInfos) {
        for (const info of contact.additionalInfos) {
          const infoTypeConfig = contactTypes.find(ct => normalizeString(ct.name) === normalizeString(info.type));
          if (infoTypeConfig?.validationKind === ContactValidationKind.PHONE || normalizeString(info.type) === normType) {
            if (matchPhoneNumbers(rawValue, info.name)) {
              return { externalContact: contact, matchedValue: info.name };
            }
          }
        }
      }
    }
  } else if (validationKind === ContactValidationKind.EMAIL) {
    const emailNorm = normalizeString(rawValue);
    if (!emailNorm || emailNorm.length < 3) return undefined;

    for (const contact of contacts) {
      if (contact.id === excludeContactId) continue;

      // Check main email
      if (contact.email && normalizeString(contact.email) === emailNorm) {
        return { externalContact: contact, matchedValue: contact.email };
      }

      // Check additionalInfos with Email validationKind or matching type
      if (contact.additionalInfos) {
        for (const info of contact.additionalInfos) {
          const infoTypeConfig = contactTypes.find(ct => normalizeString(ct.name) === normalizeString(info.type));
          if (infoTypeConfig?.validationKind === ContactValidationKind.EMAIL || normalizeString(info.type) === normType) {
            if (normalizeString(info.name) === emailNorm) {
              return { externalContact: contact, matchedValue: info.name };
            }
          }
        }
      }
    }
  } else if (validationKind === ContactValidationKind.SOCIAL) {
    const handleNorm = normalizeSocialHandle(rawValue);
    if (!handleNorm || handleNorm.length < 2) return undefined;

    for (const contact of contacts) {
      if (contact.id === excludeContactId || !contact.additionalInfos) continue;
      for (const info of contact.additionalInfos) {
        if (normalizeString(info.type) === normType) {
          const iHandle = normalizeSocialHandle(info.name);
          if (iHandle && iHandle === handleNorm) {
            return { externalContact: contact, matchedValue: info.name };
          }
        }
      }
    }
  } else if (validationKind === ContactValidationKind.URL) {
    const urlNorm = normalizeString(rawValue).replace(/^https?:\/\//, '').replace(/\/$/, '');
    if (!urlNorm || urlNorm.length < 3) return undefined;

    for (const contact of contacts) {
      if (contact.id === excludeContactId || !contact.additionalInfos) continue;
      for (const info of contact.additionalInfos) {
        if (normalizeString(info.type) === normType) {
          const iUrl = normalizeString(info.name).replace(/^https?:\/\//, '').replace(/\/$/, '');
          if (iUrl && iUrl === urlNorm) {
            return { externalContact: contact, matchedValue: info.name };
          }
        }
      }
    }
  }

  return undefined;
};
